import { combine, withCallbacks, withContext } from 'react-hooks-combine'
import { withFormModel } from 'shared/hooks'
import { ServicesContext } from 'services/context'
import { spec, validators } from 'utils/validation'

import { convertForm } from './helpers'
import AddMemberInvoiceFormComponent from './component'

const { any, string } = validators

export const AddMemberInvoiceForm = combine(
  withContext(
    'taxonomyService',
    ServicesContext,
    ({ taxonomyService }) => taxonomyService
  ),

  withContext(
    'adminMemberService',
    ServicesContext,
    ({ adminMemberService }) => adminMemberService
  ),

  withFormModel(
    () => {
      return {
        amount: {
          initialValue: '',
          spec: spec.flow(
            string.isNotEmpty,
            any.required,
            (value) => {
              const numValue = Number.parseFloat(value);
              if (Number.isNaN(numValue)) {
                return { valid: false, message: 'Please enter a valid number' };
              }
              if (numValue < 1) {
                return { valid: false, message: 'Minimum amount is 1 USD' };
              }
              return { valid: true, value: numValue };
            }
          ),
        },
        currency: {
          initialValue: '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        remarks: {
          initialValue: '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },
        bankAccountName: {
          initialValue: '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },
      }
    },
    { id: 'member-invoice-form' }
  ),

  withCallbacks(
    {
      loadBankAccounts:
        ({ adminMemberService }, { memberId }) =>
          async () => {
            const { content } = await adminMemberService.getMemberBankAccount(
              memberId
            )
            return content
          },

      // Add a new callback to set up the input validation
      setupAmountValidation:
        () =>
          () => {
            // Use setTimeout to ensure the DOM is ready
            setTimeout(() => {
              try {
                const amountInput = document.querySelector('input[name="amount"]');
                if (amountInput) {
                  // Add input event listener to validate on typing
                  amountInput.addEventListener('input', function (e) {
                    const value = e.target.value;
                    const amount = Number.parseFloat(value);

                    // Check if amount is less than 1
                    if (value && !Number.isNaN(amount) && amount < 1) {
                      // Force red border and override any other styles
                      amountInput.style.borderColor = 'red !important';
                      amountInput.style.boxShadow = '0 0 0 1px red !important';

                      // Add a class to help with styling
                      amountInput.classList.add('error-input');

                      // Remove any success classes
                      amountInput.classList.remove('success-input');

                      // Create or update error message
                      let errorMsg = amountInput.parentNode.querySelector('.error-message');
                      if (!errorMsg) {
                        errorMsg = document.createElement('div');
                        errorMsg.className = 'error-message';
                        errorMsg.style.color = 'red';
                        errorMsg.style.fontSize = '12px';
                        errorMsg.style.marginTop = '4px';
                        errorMsg.style.position = 'absolute';
                        errorMsg.style.bottom = '-15px';
                        errorMsg.style.left = '0';
                        amountInput.parentNode.appendChild(errorMsg);
                      }
                      errorMsg.textContent = 'Minimum amount is 1 USD';
                    } else {
                      // Remove error styling
                      amountInput.style.borderColor = '';
                      amountInput.style.boxShadow = '';

                      // Remove error class
                      amountInput.classList.remove('error-input');

                      const errorMsg = amountInput.parentNode.querySelector('.error-message');
                      if (errorMsg) {
                        errorMsg.remove();
                      }
                    }
                  });

                  // Also add a style tag to ensure our styles take precedence
                  const styleTag = document.createElement('style');
                  styleTag.textContent = `
                    .error-input {
                      border-color: red !important;
                      box-shadow: 0 0 0 1px red !important;
                    }
                  `;
                  document.head.appendChild(styleTag);
                }
              } catch (e) {
                console.error('Error setting up amount validation:', e);
              }
            }, 100);
          },

      onSubmitRequest: {
        deps: ['onSubmit'],
        func:
          (_state, { onSubmit }) =>
            (form) => {
              // Check if amount is valid
              const amount = Number.parseFloat(form.amount);

              // Check for minimum amount
              if (Number.isNaN(amount) || amount < 1) {
                // Direct DOM manipulation to show error
                try {
                  // Find the amount input
                  const amountInput = document.querySelector('input[name="amount"]');
                  if (amountInput) {
                    // Add red border
                    amountInput.style.borderColor = 'red';

                    // Create or update error message
                    let errorMsg = amountInput.parentNode.querySelector('.error-message');
                    if (!errorMsg) {
                      errorMsg = document.createElement('div');
                      errorMsg.className = 'error-message';
                      errorMsg.style.color = 'red';
                      errorMsg.style.fontSize = '12px';
                      errorMsg.style.marginTop = '4px';
                      errorMsg.style.position = 'absolute';
                      errorMsg.style.bottom = '-15px';
                      errorMsg.style.left = '0';
                      amountInput.parentNode.appendChild(errorMsg);
                    }
                    errorMsg.textContent = 'Minimum amount is 1 USD';
                  }
                } catch (e) {
                  console.error('Error showing validation message:', e);
                }

                // Don't proceed with submission
                return;
              }

              // Clear error styling if amount is valid
              try {
                const amountInput = document.querySelector('input[name="amount"]');
                if (amountInput) {
                  amountInput.style.borderColor = '';
                  const errorMsg = amountInput.parentNode.querySelector('.error-message');
                  if (errorMsg) {
                    errorMsg.remove();
                  }
                }
              } catch (e) {
                console.error('Error clearing validation message:', e);
              }

              if (form.currency && form.remarks && form.bankAccountName) {
                const convertedForm = convertForm(form);
                return onSubmit(convertedForm);
              }
            },
      },
    },
    []
  )
)(AddMemberInvoiceFormComponent)
